import React from 'react'
import {nanoid} from 'nanoid'
import {IconButton, Panel, Divider} from 'rsuite'
import {isFunctionalUpdate} from '../utility'
import type {JSX} from 'react'

import Th2 from '@rsuite/icons/legacy/Th2'
import Trash from '@rsuite/icons/legacy/Trash'
import Plus from '@rsuite/icons/legacy/Plus'

export interface FieldProps<V = any> {
  readonly value: V
  readonly onChange: React.Dispatch<React.SetStateAction<V>>
}

export interface ListFieldProps<T = any> extends FieldProps<ListValue<T>[]> {
  readonly maxItems?: number
  readonly label?: string
  readonly defaultValue: T | (() => T)
  readonly disabled?: boolean
  readonly disableAddItem?: boolean
  readonly children: (props: FieldProps<T>) => JSX.Element
}

export interface ListValue<T = any> {
  readonly id: string
  readonly value: T
}

export interface ListItemProps<T = any> {
  readonly value: ListValue<T>
  readonly itemIndex: number
  readonly itemDisabled?: boolean
  readonly onChange: (index: number, value: React.SetStateAction<ListValue<T>>) => void
  readonly onRemove: (index: number) => void
  readonly children: (props: FieldProps<T>) => JSX.Element
}

function ListItem<T>({
  value,
  itemIndex,
  itemDisabled,
  onChange,
  onRemove,
  children,
}: ListItemProps<T>) {
  function handleValueChange(fieldValue: React.SetStateAction<any>) {
    onChange(itemIndex, (val) => ({
      ...val,
      value: isFunctionalUpdate(fieldValue) ? fieldValue(val.value) : fieldValue,
    }))
  }

  function handleRemove() {
    onRemove(itemIndex)
  }

  return (
    <div style={{display: 'flex', flexDirection: 'row', marginBottom: '20px'}}>
      <Panel bordered bodyFill style={{width: '100%', padding: '20px'}}>
        {children({value: value.value, onChange: handleValueChange})}
      </Panel>
      <div
        style={{
          marginLeft: '10px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <IconButton size="sm" icon={<Trash />} onClick={handleRemove} disabled={itemDisabled} />
      </div>
    </div>
  )
}

export function ListInput<T>({
  maxItems,
  value,
  label,
  defaultValue,
  disabled,
  children,
  onChange,
}: ListFieldProps<T>) {
  function handleItemChange(index: number, itemValue: React.SetStateAction<ListValue>) {
    onChange((val) =>
      Object.assign([], val, {
        [index]: isFunctionalUpdate(itemValue) ? itemValue(val[index]) : itemValue,
      })
    )
  }

  function handleAdd() {
    onChange((val) => [
      ...val,
      {
        id: nanoid(),
        value: typeof defaultValue === 'function' ? (defaultValue as () => T)() : defaultValue,
      },
    ])
  }

  function handleRemove(index: number) {
    onChange((val) => val.filter((_, i) => i !== index))
  }

  const disableAddItem = !!(disabled || (maxItems !== undefined && value.length >= maxItems))

  return (
    <div>
      {label && <label>{label}</label>}
      {value.map((v, i) => (
        <ListItem
          key={v.id}
          itemIndex={i}
          value={v}
          itemDisabled={disabled}
          onChange={handleItemChange}
          onRemove={handleRemove}
        >
          {children}
        </ListItem>
      ))}
      <Divider>
        <IconButton
          size="md"
          appearance="ghost"
          circle
          icon={<Plus />}
          onClick={handleAdd}
          disabled={disableAddItem}
        />
      </Divider>
    </div>
  )
}
